import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form } from "react-bootstrap";
import { toast } from "wc-toast";
import { postInsReview, postReviewDetail, postUpdReview } from "../../api/customerCenter";
import MobileFooter from "../../componant/mobileFooter";
import { useAtom } from "jotai";
import { headerText } from "../../App";
import { postGetCommFile, postDelFile } from "../../api/mypage";

function ReviewNewEdit() {
  const { seq } = useParams();
  const isEdit = !!seq;

  const [reviewReq, setReviewReq] = useState({
    files: [],
    title: "",
    contents: "",
    nocomment_yn: "N",
  });

  const navigate = useNavigate();
  const [headerTextAtom, setHeaderTextAtom] = useAtom(headerText);
  const [previews, setPreviews] = useState([]);
  const [contentType, setContentType] = useState("");

  const MAX_FILES = 5;
  const MAX_FILE_SIZE_MB = 5;
  const MAX_TOTAL_SIZE_MB = 10;

  // 파일 가져오는 함수 (에러 처리 강화)
  const getFiles = useCallback(async (fileSeq) => {
    const data = { file_seq: fileSeq };
    try {
      const response = await postGetCommFile(data);

      if (!response || !response.data) {
        return null;
      }

      const contentType = response.headers?.["content-type"] || "image/png";
      const blob = new Blob([response.data], { type: contentType });
      const previewUrl = URL.createObjectURL(blob);

      setContentType(contentType);
      return { url: previewUrl };
    } catch (error) {
      console.error("파일 로드 실패:", error);
      return null; // 실패 시 null 반환하여 에러 방지
    }
  }, []);

  const callReviewData = async () => {
    try {
      const data = { review_seq: seq };
      const response = await postReviewDetail(data);
      if (response.code === 1000) {
        setReviewReq(response.result);
        const files = response?.result?.files || [];

        const previewResults = await Promise.all(
          files.map((item) => getFiles(item.file_seq))
        );

        // url이 정상적으로 생성된 객체만 필터링
        const validPreviews = previewResults.filter((item) => item !== null && item?.url);
        setPreviews(validPreviews);
      }
    } catch (error) {
      alert("상세 정보를 가져오지 못했습니다.");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    const allowedExtensions = ["jpg", "jpeg", "png"];
    if (files?.length > 0) {
      const extension = files[0].name.split(".").pop().toLowerCase();
      if (!allowedExtensions.includes(extension)) {
        return toast.error("jpg, jpeg, png 파일만 첨부할 수 있습니다.");
      }
    }

    let newValue = null;

    // 파일업로드 시
    if (name === "files") {
      const selectedFiles = Array.from(e.target.files);

      if ((reviewReq?.files?.length || 0) + (selectedFiles?.length || 0) > MAX_FILES) {
        toast.error(`최대 ${MAX_FILES}개까지 등록할 수 있습니다.`);
        return;
      }
      // 파일 하나 사이즈 체크 5MB
      const tooLargeFile = selectedFiles.find(file => file.size > MAX_FILE_SIZE_MB * 1024 * 1024);
      if (tooLargeFile) {
        toast.error(`파일당 최대 ${MAX_FILE_SIZE_MB}MB까지만 등록할 수 있습니다.`);
        return;
      }
      // 파일 전체 사이즈 체크 10MB
      const totalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);
      if (totalSize > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
        toast.error(`전체 파일 용량은 최대 ${MAX_TOTAL_SIZE_MB}MB를 넘을 수 없습니다.`);
        return;
      }

      // 새 파일 미리보기 URL 생성
      const newPreviews = selectedFiles.map((file) => ({
        url: URL.createObjectURL(file)
      }));

      // 기존 파일과 새 파일을 합쳐서 상태 업데이트
      newValue = [...(reviewReq?.files || []), ...selectedFiles];
      setPreviews((prev) => [...prev, ...newPreviews]);
    } else {
      newValue = type === "checkbox" ? (checked ? "Y" : "N") : value;
    }

    setReviewReq((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleRemove = async (index) => {
    // URL 해제 (존재할 경우)
    if (previews[index]?.url) {
      URL.revokeObjectURL(previews[index].url);
    }

    // file_seq 가진 것은 DB에서도 삭제
    const fileSeq = reviewReq.files?.[index]?.file_seq;
    if (fileSeq) {
      await postDelFile({ file_seq: fileSeq });
    }

    // 상태에서 해당 인덱스 제거
    setPreviews((prev) => prev.filter((_, i) => i !== index));
    setReviewReq((prev) => ({
      ...prev,
      files: prev?.files?.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    if (!localStorage.getItem("access_token")) {
      return toast.error("로그인 후 이용가능 합니다.", {
        duration: 500,
      });
    }
    if (!reviewReq.title || reviewReq.title === "") {
      return toast.error("제목을 입력해 주세요.");
    }
    if (!reviewReq.contents || reviewReq.contents === "") {
      return toast.error("내용을 입력해 주세요.");
    }

    try {
      if (isEdit) {
        const response = await postUpdReview(reviewReq);
        if (response.code !== 1000) {
          return console.log("수정 실패");
        }
      } else {
        const response = await postInsReview(reviewReq);
        if (response.code !== 1000) {
          return console.log("등록 실패");
        }
      }
      toast.success(isEdit ? "수정되었습니다" : "등록되었습니다.");
      setReviewReq({
        files: [],
        title: "",
        contents: "",
        nocomment_yn: "N",
      });
      navigate("/announcement", { state: 3 });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("access_token")) {
      toast.error("로그인 후 이용가능 합니다.", {
        duration: 500,
      });
    }
    setHeaderTextAtom(isEdit ? "이용후기\u00A0\u00A0수정하기" : "이용후기\u00A0\u00A0등록하기");

    if (isEdit) {
      callReviewData();
    } else {
      setReviewReq({
        files: [],
        title: "",
        contents: "",
        nocomment_yn: "N",
      });
    }
  }, []);

  return (
    <>
      <div className="customer_tab bg-white" style={{ paddingTop: "10px" }}>
        <div className="container">
          <div className="main-heading d-md-block d-none">
            <h3 className="text-left">이용후기 등록하기</h3>
          </div>
          <hr className="information-hr pc_view" />
          <div className="question_answer" style={{ paddingTop: "20px" }}>
            <div className="subject">
              <input
                type="text"
                className="w-100"
                placeholder="제목을 입력해 주세요"
                name="title"
                value={reviewReq?.title || ""}
                onChange={handleChange}
                maxLength={150}
              />
              <span className="title">{reviewReq?.title?.length || 0}/150자</span>
            </div>
            <div className="caution">
              ※ 이용후기의 경우 차량사진, 번호판 사진 등 3장 이상의 사진을 함께 업로드 해주시면
              다른 유저들에게 큰 도움이 됩니다. <br />
              실제 거래와 다른 허위 사실이거나 비방일 경우 관리자가 작성자의 허락과 관계없이 임의로 삭제할 수 있습니다.
            </div>
            <div className="detail">
              <textarea
                className="w-100"
                placeholder="내용을 입력해 주세요"
                name="contents"
                value={reviewReq?.contents || ""}
                onChange={handleChange}
                maxLength={1000}
              ></textarea>
              <span className="title">{reviewReq?.contents?.length || 0}/1,000자</span>
            </div>
            <div className="sub_title">
              <p>
                첨부파일 <span>(선택)</span>
              </p>
            </div>
            <input
              type="file"
              id="upload-review"
              className="w-100 d-none"
              name="files"
              multiple
              accept=".jpg, .jpeg, .png"
              onChange={handleChange}
            />
            <div className="attached_file d-flex">
              <label
                htmlFor="upload-review"
                style={{
                  display: "block",
                  width: "40px",
                  cursor: "pointer"
                }}
              >
                <img className="plus" src="/img/plus.png" alt="fileuploadBtn" />
                <img className="attachment" src="/img/attach.svg" alt="fileuploadImg" />
              </label>
              <span className="title">
                첨부가능파일 : jpg, jpeg, png <br />
                최대 5개 등록 가능 <span style={{ fontSize: "11px" }}>(파일당 5MB / 전체파일 10MB 까지)</span>
              </span>
            </div>

            {/* 이미지 미리보기 구역 */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '15px' }}>
              {previews.map((preview, index) => {
                if (!preview || !preview.url) return null;

                return (
                  <div key={index} style={{ position: 'relative', width: '80px', height: '80px' }}>
                    <img
                      src={preview.url}
                      alt={`preview-${index}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '5px' }}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemove(index)}
                      style={{
                        position: 'absolute',
                        top: '-5px',
                        right: '-5px',
                        background: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer',
                        lineHeight: '18px',
                        fontSize: '14px',
                        padding: 0
                      }}
                      title="삭제"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="main-btn d-flex justify-content-center">
              <button onClick={handleSubmit}>{isEdit ? "수정하기" : "등록하기"}</button>
            </div>
          </div>
        </div>
      </div>
      <MobileFooter main={true} />
    </>
  );
}

export default ReviewNewEdit;